import {
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useSetTaskDoneMutation,
  useUpdateTaskMutation,
} from 'gcs-common/clients/api/entities/tasks/tasks-api';
import type { FetchTasksResponseData } from 'gcs-common/clients/api/entities/tasks/tasks-api-helpers';
import {
  useCreateTaskMutationCache,
  useSetTaskDoneMutationCache,
  useUpdateTaskMutationCache,
} from 'gcs-common/clients/api/entities/tasks/tasks-mutations-cache-selectors';
import IconComponent from 'gcs-common/components/Icon/Icon';
import { LEGACY_EVENTS } from 'gcs-common/config/googleAnalyticsLegacyConfig';
import { ICON, ICON_COLOR } from 'gcs-common/constants/IconConstants';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import scrollToMessageId from 'gcs-common/slices/chat/chatThunks/scrollToMessageId';
import { openAsMailToEmail } from 'gcs-common/slices/email/emailThunks';
import { trackGALegacyEvent } from 'gcs-common/slices/googleAnalytics/googleAnalyticsThunks';
import { INVALID_ID } from 'gcs-common/slices/tasks/tasksConstants';
import { getSelectedTaskId } from 'gcs-common/slices/tasks/tasksSelectors';
import { taskIdSelected } from 'gcs-common/slices/tasks/tasksSlice';
import { type Dispatch, type SetStateAction, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import CheckBox from '../CheckBox/CheckBox';
import PopupMenu from '../PopupMenu/PopupMenu';
import TaskDone from './TaskDone/TaskDone';
import TaskEdit from './TaskEdit/TaskEdit';
import TaskTodo from './TaskTodo/TaskTodo';
import styles from './styles.module.scss';

interface TaskProps {
  task: FetchTasksResponseData['tasks'][number];
  dark: boolean;
  setTaskDueSelect: Dispatch<SetStateAction<FetchTasksResponseData['tasks'][number] | null>>;
  isDragging?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

const Task = (props: TaskProps) => {
  const { task, dark, setTaskDueSelect, dragHandleProps, isDragging = false } = props;
  const { id: taskId } = task;
  const selectedChannelId = useSelector(getSelectedChannelId);

  const isNewCreatedTask = taskId === INVALID_ID;
  const pendingCreateTaskMutations = useCreateTaskMutationCache({
    status: 'pending',
  });
  const pendingUpdateTaskMutations = useUpdateTaskMutationCache({
    status: 'pending',
  });
  const pendingSetTaskDoneMutations = useSetTaskDoneMutationCache({
    status: 'pending',
  });
  const isTaskPending =
    pendingCreateTaskMutations.includes(taskId) ||
    pendingUpdateTaskMutations.includes(taskId) ||
    pendingSetTaskDoneMutations.includes(taskId);

  const ref = useRef<HTMLDivElement>(null);

  const [isDraggable, setDraggable] = useState(true);
  const [editMenuVisible, setEditMenuVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const selectedTaskId = useSelector(getSelectedTaskId);
  const createTaskMutation = useCreateTaskMutation();
  const updateTaskMutation = useUpdateTaskMutation();
  const deleteTaskMutation = useDeleteTaskMutation({
    channelId: selectedChannelId,
  });
  const setTaskDoneMutation = useSetTaskDoneMutation();
  const dispatch = useDispatch();

  const isSelected = selectedTaskId === taskId;

  const selectTask = (taskToSelect?: FetchTasksResponseData['tasks'][number]) => {
    dispatch(taskIdSelected({ taskId: taskToSelect ? taskToSelect.id : undefined }));
  };

  const onScrollToMessage = () => {
    if (!isSelected) {
      selectTask(task);
    }
    const { messageId } = task;
    dispatch(
      // @ts-ignore
      scrollToMessageId({
        channelId: selectedChannelId,
        messageId,
      }),
    );
  };

  const toggleDone = () => {
    if (!isNewCreatedTask) {
      setTaskDoneMutation.mutate({
        params: {
          path: {
            task_id: task.id,
          },
        },
        body: {
          done: !task.done,
          channelId: selectedChannelId,
        },
      });
      selectTask();
    }
  };

  const handleSubmit = (newText: string) => {
    if (isNewCreatedTask && newText !== '') {
      createTaskMutation.mutate({
        body: {
          id: uuidv4(),
          channelId: selectedChannelId ?? '',
          description: newText,
          done: false,
        },
      });
    } else if (task.description !== newText) {
      dispatch(trackGALegacyEvent(LEGACY_EVENTS.EDIT_TASK()));
      updateTaskMutation.mutate({
        params: {
          path: {
            task_id: task.id,
          },
        },
        body: {
          ...task,
          description: newText,
          channelId: selectedChannelId,
        },
      });
    }
    selectTask();
  };

  const handleDelete = () => {
    // not a dummy task
    if (task.id !== INVALID_ID) {
      deleteTaskMutation.mutate({
        params: {
          path: {
            task_id: task.id,
          },
        },
      });
    }
    selectTask();
  };

  const handleTaskDueSelect = () => setTaskDueSelect(task);

  const forwardEmail = () => {
    dispatch(trackGALegacyEvent(LEGACY_EVENTS.TODO_FORWARD_EMAIL()));
    const { description: body } = task;
    dispatch(openAsMailToEmail({ subject: '', body }));
  };

  const bgColor = isSelected || isDragging ? '#f5f5f5' : '';

  useEffect(() => {
    if (!isSelected) {
      setEditMenuVisible(false);
    }
  }, [isSelected]);

  useLayoutEffect(() => {
    if (ref.current) {
      if (isDraggable) {
        ref.current.setAttribute('draggable', 'true');
      } else {
        ref.current.setAttribute('draggable', 'false');
      }
    }
  }, [isDraggable]);

  const handleEditMenuClicked = () => {
    setEditMenuVisible(true);
    if (!isSelected) {
      dispatch(taskIdSelected({ taskId: undefined }));
    }
  };

  return (
    <div
      className={`${styles.todoWrapper} ${dark ? styles.dark : ''}`}
      style={{ backgroundColor: bgColor }}
      ref={ref}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`${styles.todo}`}>
        <CheckBox onClick={toggleDone} isChecked={task.done} color={ICON_COLOR.DARK_GREY} />
        <div className={`${styles.descriptionSection} ${isTaskPending ? styles.isPending : ''}`}>
          {isSelected && !task.done ? (
            <TaskEdit
              parentRef={ref}
              handleSubmit={handleSubmit}
              currText={task.description}
              onMouseOver={() => setDraggable(false)}
              onMouseOut={() => setDraggable(true)}
            />
          ) : task.done ? (
            <TaskDone task={task} clickAction={() => selectTask(task)} />
          ) : (
            <TaskTodo task={task} clickAction={() => selectTask(task)} />
          )}
        </div>
        <div className={styles.sideBar}>
          {!task.done && task.dueDate && (
            <button type="button" onClick={handleTaskDueSelect} className={styles.iconButton}>
              <IconComponent Icon={ICON.ALARM} color={ICON_COLOR.LIGHT_GREY} alt="clock-icon" />
            </button>
          )}
          {!task.done && task.messageId && (
            <button type="button" onClick={onScrollToMessage} className={styles.iconButton}>
              <IconComponent Icon={ICON.CHAT} color={ICON_COLOR.LIGHT_GREY} alt="msg-icon" />
            </button>
          )}

          {!isNewCreatedTask && (isSelected || editMenuVisible || (!task.done && isHovered)) && (
            <>
              <button type="button" onClick={handleEditMenuClicked}>
                <IconComponent Icon={ICON.MORE} color={ICON_COLOR.LIGHT_GREY} alt="todo-menu" />
              </button>
              <PopupMenu
                onHide={() => setEditMenuVisible(false)}
                isVisible={editMenuVisible}
                menuClass={`${styles.editMenuPopup} ${task.done ? styles.doneEditMenuPopup : ''}`}
              >
                {task.messageId && (
                  <button type="button" onClick={onScrollToMessage}>
                    <IconComponent Icon={ICON.CHAT} alt="msg-icon" />
                    zur Nachricht gehen
                  </button>
                )}
                <button type="button" onClick={handleTaskDueSelect}>
                  <IconComponent Icon={ICON.ALARM} alt="clock-icon" />
                  Zeitpunkt auswählen
                </button>
                <button type="button" onClick={handleDelete}>
                  <IconComponent Icon={ICON.DELETE} alt="delete-todo" />
                  To Do löschen
                </button>
                <button type="button" onClick={forwardEmail}>
                  <IconComponent Icon={ICON.EMAIL} alt="forward-email-todo" />
                  Email
                </button>
              </PopupMenu>
            </>
          )}
          {!task.done && (
            <button type="button" {...dragHandleProps}>
              <IconComponent
                Icon={ICON.DRAG}
                color={task.done ? ICON_COLOR.DARK_GREY : ICON_COLOR.LIGHT_GREY}
                alt="move-todo"
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Task;
